import { useQuery } from "convex/react";
import { Send, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "motion/react";

import { friendlyAppName } from "../../lib/apps/friendlyAppName";
import { insightLocalChatReply, mapInsightChatError } from "../../lib/ai/insightChatPrompt";
import { queryRef } from "../../lib/convex/refs";
import {
  hasInsightChatContextUsageData,
  serializeInsightChatContextForChat,
  type InsightChatContextPayload,
} from "../../lib/insights/insightChatContext";
import type { LocalAIState } from "../../lib/native/ai";
import type { ChatTurn } from "../../lib/native/ai";
import {
  keyboardAwareOverlayPadding,
  keyboardAwareSheetMaxHeight,
} from "../../lib/native/keyboardInset";
import { useKeyboardInsetWhen } from "../../lib/native/KeyboardInsetProvider";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import { pickRandomSuggestions } from "./insightChatSuggestions";

const CONTEXT_LOAD_TIMEOUT_MS = 15_000;
const SUGGESTION_COUNT = 3;

type InsightChatSheetProps = {
  open: boolean;
  onClose: () => void;
  anonymousDeviceId: string;
  startDate: string;
  endDate: string;
  excludedAppKeys: string[];
  aiState: LocalAIState | null;
};

export function InsightChatSheet({
  open,
  onClose,
  anonymousDeviceId,
  startDate,
  endDate,
  excludedAppKeys,
  aiState,
}: InsightChatSheetProps) {
  const prefersReducedMotion = useReducedMotion();
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const [messages, setMessages] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [contextRetry, setContextRetry] = useState(0);
  const [contextTimedOut, setContextTimedOut] = useState(false);
  const [suggestionSession, setSuggestionSession] = useState(0);
  const greetedRef = useRef(false);
  const sheetRef = useRef<HTMLElement>(null);
  const keyboardState = useKeyboardInsetWhen(open);
  const overlayPaddingBottom = keyboardAwareOverlayPadding(keyboardState.bottomInset);
  const sheetMaxHeight = keyboardAwareSheetMaxHeight("82svh", keyboardState);

  const context = useQuery(
    queryRef<
      {
        anonymousDeviceId: string;
        startDate: string;
        endDate: string;
        excludedAppKeys: string[];
        languageCode: string;
        cacheBust?: number;
      },
      InsightChatContextPayload | undefined
    >("usageInsights:getInsightChatContext"),
    open
      ? {
          anonymousDeviceId,
          startDate,
          endDate,
          excludedAppKeys,
          languageCode,
          cacheBust: contextRetry > 0 ? contextRetry : undefined,
        }
      : "skip",
  );

  const contextJson = useMemo(
    () =>
      context && hasInsightChatContextUsageData(context)
        ? serializeInsightChatContextForChat(context, languageCode)
        : null,
    [context, languageCode],
  );

  const topAppName = useMemo(() => {
    const metric = context?.scores.compulsivityTop[0] ?? context?.scores.doomScrollRiskTop[0];
    return metric ? friendlyAppName(metric) : null;
  }, [context]);

  const suggestionPool = useMemo(() => {
    const whyPill = topAppName
      ? formatTemplate(t.insightChat.suggestionTopAppWhy, { appName: topAppName })
      : t.insightChat.suggestionTopAppWhyGeneric;
    return [
      t.insightChat.suggestionTotalUsage,
      t.insightChat.suggestionTopApp,
      t.insightChat.suggestionBusiestDays,
      t.insightChat.suggestionBusiestBands,
      whyPill,
      t.insightChat.suggestionComparePeriods,
    ];
  }, [t.insightChat, topAppName]);

  const visibleSuggestions = useMemo(
    () => pickRandomSuggestions(suggestionPool, SUGGESTION_COUNT),
    [suggestionPool, suggestionSession],
  );

  const hasUserMessage = messages.some((message) => message.role === "user");

  useEffect(() => {
    if (!open) {
      greetedRef.current = false;
      setMessages([]);
      setInput("");
      setBusy(false);
      setContextRetry(0);
      setContextTimedOut(false);
      return;
    }
    setSuggestionSession((current) => current + 1);
    if (!greetedRef.current) {
      greetedRef.current = true;
      setMessages([{ role: "assistant", text: t.insightChat.greeting }]);
    }
  }, [open, t.insightChat.greeting]);

  useEffect(() => {
    if (!open) return;
    if (context !== undefined) {
      setContextTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => setContextTimedOut(true), CONTEXT_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [open, context, contextRetry]);

  useEffect(() => {
    if (!open) return;
    const sheet = sheetRef.current;
    if (!sheet) return;

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.matches("input, textarea, select, [contenteditable='true']")) return;
      requestAnimationFrame(() => {
        target.scrollIntoView({ block: "nearest", behavior: prefersReducedMotion ? "auto" : "smooth" });
      });
    };

    sheet.addEventListener("focusin", onFocusIn);
    return () => sheet.removeEventListener("focusin", onFocusIn);
  }, [open, prefersReducedMotion]);

  async function send(text = input) {
    const trimmed = text.trim();
    if (!trimmed || busy || !contextJson) return;

    const history = messages.filter(
      (message, index) => !(index === 0 && message.role === "assistant"),
    );
    setInput("");
    setBusy(true);
    setMessages((current) => [...current, { role: "user", text: trimmed }]);

    try {
      const reply = await insightLocalChatReply(trimmed, contextJson, history, languageCode);
      setMessages((current) => [...current, { role: "assistant", text: reply }]);
    } catch (error) {
      const message = mapInsightChatError(error, languageCode);
      setMessages((current) => [...current, { role: "assistant", text: message }]);
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  const contextLoading = context === undefined && !contextTimedOut;
  const contextFailed = context === undefined && contextTimedOut;
  const contextEmpty = context !== undefined && !hasInsightChatContextUsageData(context);
  const contextReady = contextJson !== null;
  const inputDisabled = busy || !contextReady || contextFailed || contextEmpty;
  const showSuggestions = contextReady && !hasUserMessage && !busy;
  const engineLabel = aiState?.platform === "ios"
    ? t.insightChat.engineIosLocal
    : t.insightChat.engineAndroidLocal;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end bg-black/45"
      style={overlayPaddingBottom !== undefined ? { paddingBottom: overlayPaddingBottom } : undefined}
      onClick={onClose}
    >
      <section
        ref={sheetRef}
        className="liquid-glass-sheet flex min-h-0 flex-col overflow-hidden rounded-t-lg p-4 pb-[max(18px,env(safe-area-inset-bottom))]"
        style={{ maxHeight: sheetMaxHeight }}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold">{t.insightChat.title}</h2>
            <p className="mt-1 text-xs text-[oklch(68%_0.025_155)]">{engineLabel}</p>
          </div>
          <button
            type="button"
            className="grid min-h-11 min-w-11 shrink-0 place-items-center rounded-md"
            onClick={onClose}
            aria-label={t.insightChat.closeAria}
          >
            <X size={20} />
          </button>
        </header>

        <div className="mb-4 grid min-h-0 flex-1 gap-3 overflow-y-auto">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`max-w-[88%] rounded-md p-3 text-sm leading-6 ${
                message.role === "user"
                  ? "ml-auto bg-[oklch(28%_0.055_160)]"
                  : "bg-[oklch(21%_0.018_165)]"
              }`}
            >
              {message.text || (busy ? "…" : "")}
            </div>
          ))}
          {contextFailed ? (
            <div className="space-y-3">
              <p className="text-sm text-[oklch(72%_0.025_155)]">{t.insightChat.contextError}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="liquid-glass-suggestion min-h-10 px-3 text-sm"
                  onClick={() => {
                    setContextTimedOut(false);
                    setContextRetry((current) => current + 1);
                  }}
                >
                  {t.insightChat.retryContext}
                </button>
                <button
                  type="button"
                  className="liquid-glass-suggestion min-h-10 px-3 text-sm"
                  onClick={onClose}
                >
                  {t.insightChat.closeAria}
                </button>
              </div>
            </div>
          ) : contextEmpty ? (
            <div className="space-y-3">
              <p className="text-sm text-[oklch(72%_0.025_155)]">{t.insightChat.contextEmpty}</p>
              <button
                type="button"
                className="liquid-glass-suggestion min-h-10 px-3 text-sm"
                onClick={onClose}
              >
                {t.insightChat.closeAria}
              </button>
            </div>
          ) : contextLoading ? (
            <p className="text-sm text-[oklch(72%_0.025_155)]">{t.insightChat.contextLoading}</p>
          ) : null}
          {busy && messages[messages.length - 1]?.role !== "assistant" ? (
            <div className="text-sm text-[oklch(72%_0.025_155)]">{t.common.thinking}</div>
          ) : null}
        </div>

        {showSuggestions ? (
          <div className="mb-4 flex flex-wrap gap-2 pt-2">
            {visibleSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className="liquid-glass-suggestion min-h-10 px-3 text-sm"
                onClick={() => void send(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        ) : null}

        <form
          className="flex shrink-0 gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <input
            className="liquid-glass-input min-h-12 flex-1 rounded-md bg-transparent px-3"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={t.insightChat.placeholder}
            disabled={inputDisabled}
          />
          <button
            type="submit"
            className="grid min-h-12 min-w-12 place-items-center rounded-md bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)] disabled:opacity-50"
            aria-label={t.insightChat.sendAria}
            disabled={inputDisabled}
          >
            <Send size={18} />
          </button>
        </form>
      </section>
    </div>
  );
}
